export interface InvoiceRequest {
  id: string;
  status: 'draft' | 'submitted' | 'processed';
  createdAt: string;
  updatedAt: string;
  data: InvoiceFormData;
}

export interface InvoiceFormData {
  purchaseInfo: {
    folio?: string;
    purchaseDate?: string;
    amount?: number;
  };
  contact: {
    email?: string;
    phone?: string;
    name?: string;
  };
  fiscal: {
    rfc: string;
    razonSocial: string;
    regimenFiscal: string;
    usoCfdi: string;
    codigoPostal: string;
    metodoPago: string;
    formaPago: string;
    emailFacturacion?: string;
  };
  attachments: {
    ticket?: FileInfo;
    constanciaFiscal?: FileInfo;
  };
}

export interface FileInfo {
  name: string;
  type: string;
  size: number;
  data: string;
}

export type StepId = 'welcome' | 'purchase' | 'contact' | 'fiscal' | 'attachments' | 'review';

export interface Step {
  id: StepId;
  title: string;
}

export const STEPS: Step[] = [
  { id: 'welcome', title: 'Bienvenida' },
  { id: 'purchase', title: 'Datos de compra' },
  { id: 'contact', title: 'Datos de contacto' },
  { id: 'fiscal', title: 'Datos fiscales' },
  { id: 'attachments', title: 'Adjuntos' },
];

export const REGIMEN_FISCAL_OPTIONS = [
  { value: '601', label: '601 - General de Ley Personas Morales' },
  { value: '603', label: '603 - Personas Morales con Fines No Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { value: '606', label: '606 - Arrendamiento de Inmuebles' },
  { value: '607', label: '607 - Régimen de Enajenación o Adquisición de Bienes' },
  { value: '608', label: '608 - Demás Ingresos' },
  { value: '609', label: '609 - Consolidación' },
  { value: '610', label: '610 - Residentes del Extranjero' },
  { value: '611', label: '611 - Ingresos por Dividendos (socios y accionistas)' },
  { value: '612', label: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
  { value: '614', label: '614 - Ingresos por Intereses' },
  { value: '615', label: '615 - Régimen de los Ingresos por Ganancia de Capital' },
  { value: '616', label: '616 - Pago de Dividendos o Utilidades en Actionsiones' },
  { value: '617', label: '617 - ISR Sector Hidráulico' },
  { value: '618', label: '618 - ISR enajenación de acciones en bolsa de valores' },
  { value: '620', label: '620 - Sociedades Cooperativas de Producción' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '622', label: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { value: '623', label: '623 - Opcional para Grupos de Sociedades' },
  { value: '624', label: '624 - Coordinados' },
  { value: '625', label: '625 - Régimen de las Personas Físicas con Ingresos Provenientes de Arrendamiento de Inmuebles' },
  { value: '626', label: '626 - Régimen Simplificado de Confianza' },
];

export const USO_CFDI_OPTIONS = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I02', label: 'I02 - Mobilario y equipos de oficina por inversiones' },
  { value: 'I03', label: 'I03 - Equipo de transporte' },
  { value: 'I04', label: 'I04 - Equipo de cómputo y accesorios' },
  { value: 'I05', label: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
  { value: 'I06', label: 'I06 - Telecomunicaciones' },
  { value: 'I07', label: 'I07 - Servicios profesionales' },
  { value: 'P01', label: 'P01 - Por definir' },
  { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
  { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
  { value: 'D03', label: 'D03 - Gastos funerales' },
  { value: 'D04', label: 'D04 - Donativos' },
  { value: 'D05', label: 'D05 - Intereses reales efectivamente pagados por créditos hipotecarios' },
  { value: 'D06', label: 'D06 - Aportaciones voluntarias al SAR' },
  { value: 'D07', label: 'D07 - Primas por seguros de gastos médicos' },
  { value: 'D08', label: 'D08 - Gastos de transportación escolar obligatoria' },
  { value: 'D09', label: 'D09 - Pagos por servicios educativos (colegiaturas)' },
  { value: 'D10', label: 'D10 - Aplicación de суммы' },
];

export const METODO_PAGO_OPTIONS = [
  { value: 'PUE', label: 'PUE - Pago en una sola exhibición' },
  { value: 'PPD', label: 'PPD - Pago diferido o parcialidades' },
];

export const FORMA_PAGO_OPTIONS = [
  { value: '01', label: '01 - Efectivo' },
  { value: '02', label: '02 - Cheque nominativo' },
  { value: '03', label: '03 - Transferencia electrónica de fondos' },
  { value: '04', label: '04 - Tarjeta de crédito' },
  { value: '05', label: '05 - Monedero electrónico' },
  { value: '06', label: '06 - Dinero electrónico' },
  { value: '08', label: '08 - Vales de despensa' },
  { value: '12', label: '12 - Dación de pago' },
  { value: '13', label: '13 - Pago por subrogación' },
  { value: '14', label: '14 - Pago por consignación' },
  { value: '15', label: '15 - Condonación' },
  { value: '17', label: '17 - Compensación' },
  { value: '23', label: '23 - Novación' },
  { value: '24', label: '24 - Confusión' },
  { value: '25', label: '25 - Remisión de deuda' },
  { value: '26', label: '26 - Prescripción o caducidad' },
  { value: '27', label: '27 - A satisfacción del acreedor' },
  { value: '28', label: '28 - Tarjeta de débito' },
  { value: '29', label: '29 - Tarjeta de servicios' },
  { value: '30', label: '30 - Aplicación de anticipos' },
  { value: '31', label: '31 - Intermediario de pagos' },
  { value: '99', label: '99 - Por definir' },
];
